import React, { useEffect, useMemo, useState } from "react"
import Select from "react-select"
import {
  Card, CardBody, Row, Col, Input, Table, Button,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import {
  getFleetMaintenance, saveFleetMaintenanceLog, removeFleetMaintenanceLog,
  postFleetMaintenanceVoucher, getVehicles, getFleetPaymentLedgers,
} from "../../services/fleet"
import { toastError, toastSuccess } from "../../helpers/msg_helper"
import useCollapsedSidebar from "../../hooks/useCollapsedSidebar"

const money = v => (v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
const fmtDate = s => (s ? String(s).slice(0, 10).split("-").reverse().join("-") : "—")
const VAT_RATE = 0.05
const blankForm = () => ({
  id: null, vehicle_id: "", entry_date: new Date().toISOString().slice(0, 10), service_date: "",
  driver_name: "", invoice_no: "", description: "", service_type: "", odometer: "", last_service_km: "",
  amount: "", vat: "", total: "", remarks: "", payment_ledger_id: "",
})

/** Operations → Fleet → Maintenance. Service entries per vehicle; each can auto-post a Bank/Cash
 *  Payment voucher (Dr maintenance + VAT, Cr bank/cash) to the common book. */
const FleetMaintenance = () => {
  useCollapsedSidebar()
  document.title = "Vehicle Maintenance | Calyvern"
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7))
  const [data, setData] = useState({ logs: [], total: 0, count: 0 })
  const [loading, setLoading] = useState(false)
  const [vehicles, setVehicles] = useState([])
  const [payLedgers, setPayLedgers] = useState([])
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(blankForm())
  const [saving, setSaving] = useState(false)
  const [vehCompany, setVehCompany] = useState("")
  const [postFor, setPostFor] = useState(null)
  const [postLedger, setPostLedger] = useState("")
  const [posting, setPosting] = useState(false)

  const load = async () => {
    try { setLoading(true); const r = await getFleetMaintenance({ month }); setData(r?.data || { logs: [], total: 0 }) }
    catch (e) { toastError(e?.message || "Failed to load maintenance") } finally { setLoading(false) }
  }
  useEffect(() => { load() /* eslint-disable-next-line */ }, [month])
  useEffect(() => { (async () => {
    try { const r = await getVehicles({ status: 1 }); setVehicles(r?.data || []) } catch (e) {}
    try { const r = await getFleetPaymentLedgers(); setPayLedgers(r?.data || []) } catch (e) {}
  })() }, [])

  const logs = data.logs || []
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const vehLabel = v => `${(v.plate_code ? v.plate_code + "-" : "")}${v.plate_no}${v.name_of_vehicle ? ` · ${v.name_of_vehicle}` : ""}`
  const selectedVehicle = vehicles.find(v => String(v.id) === String(form.vehicle_id))

  const companies = useMemo(() => {
    const m = new Map()
    vehicles.forEach(v => { if (v.owner_company_id && !m.has(v.owner_company_id)) m.set(v.owner_company_id, v.owner?.code || v.owner?.company_name || `Company ${v.owner_company_id}`) })
    return [...m.entries()].map(([id, name]) => ({ id, name }))
  }, [vehicles])
  const vehicleOptions = useMemo(
    () => vehicles.filter(v => !vehCompany || String(v.owner_company_id) === String(vehCompany)).map(v => ({ value: v.id, label: vehLabel(v) })),
    [vehicles, vehCompany]
  )

  // amount → VAT (5%) → total, kept in sync unless the user overrides VAT/total
  const setAmount = val => setForm(f => {
    const amt = Number(val || 0)
    const vat = +(amt * VAT_RATE).toFixed(2)
    return { ...f, amount: val, vat: String(vat), total: String(+(amt + vat).toFixed(2)) }
  })
  const setVat = val => setForm(f => ({ ...f, vat: val, total: String(+((Number(f.amount || 0)) + Number(val || 0)).toFixed(2)) }))

  const openAdd = () => { setForm(blankForm()); setModal(true) }
  const openEdit = l => { setForm({
    id: l.id, vehicle_id: String(l.vehicle_id), entry_date: String(l.entry_date || "").slice(0, 10),
    service_date: String(l.service_date || "").slice(0, 10), driver_name: l.driver_name || "", invoice_no: l.invoice_no || "",
    description: l.description || "", service_type: l.service_type || "", odometer: l.odometer ?? "", last_service_km: l.last_service_km ?? "",
    amount: l.amount ?? "", vat: l.vat ?? "", total: l.total ?? "", remarks: l.remarks || "", payment_ledger_id: "",
  }); setModal(true) }
  const pickVehicle = id => {
    const v = vehicles.find(x => String(x.id) === String(id))
    setForm(f => ({ ...f, vehicle_id: id, driver_name: f.driver_name || v?.driver_name || "" }))
  }
  const save = async () => {
    if (!form.vehicle_id) return toastError("Vehicle is required")
    try { setSaving(true); const r = await saveFleetMaintenanceLog(form); toastSuccess(r?.message || "Saved"); setModal(false); await load() }
    catch (e) { toastError(e?.message || "Save failed") } finally { setSaving(false) }
  }
  const remove = async l => { if (!window.confirm(`Remove maintenance entry for ${l.plate}?`)) return; try { await removeFleetMaintenanceLog(l.id); toastSuccess("Removed"); await load() } catch (e) { toastError(e?.message) } }
  const openPost = l => { setPostFor(l); setPostLedger("") }
  const doPost = async () => {
    if (!postLedger) return toastError("Choose a bank/cash ledger")
    try { setPosting(true); const r = await postFleetMaintenanceVoucher(postFor.id, postLedger); toastSuccess(r?.message || "Voucher posted"); setPostFor(null); await load() }
    catch (e) { toastError(e?.message || "Could not post voucher") } finally { setPosting(false) }
  }

  const payFromSelect = (value, onChange) => (
    <Input type="select" bsSize="sm" value={value} onChange={onChange}>
      <option value="">Don’t post a voucher</option>
      {payLedgers.some(l => l.kind === "bank") && <optgroup label="Bank">{payLedgers.filter(l => l.kind === "bank").map(l => <option key={l.value} value={l.value}>{l.label}</option>)}</optgroup>}
      {payLedgers.some(l => l.kind === "cash") && <optgroup label="Cash">{payLedgers.filter(l => l.kind === "cash").map(l => <option key={l.value} value={l.value}>{l.label}</option>)}</optgroup>}
    </Input>
  )

  return (
    <div className="page-content tnt-scope">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Breadcrumbs title="Fleet" breadcrumbObject={[{ title: "Fleet", link: "/fleet-dashboard" }, { title: "Maintenance", link: "/fleet-maintenance" }]} />
          <div className="d-flex align-items-center gap-2">
            <Input type="month" bsSize="sm" style={{ width: 160 }} value={month} onChange={e => setMonth(e.target.value)} />
            <Button size="sm" color="primary" onClick={openAdd}><i className="bx bx-plus me-1" /> Maintenance Entry</Button>
          </div>
        </div>

        <Card className="mb-3"><CardBody>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="fw-semibold mb-0">Service Entries</h6>
            <span className="text-muted small">Total: <b>{money(data.total)} AED</b> · {data.count || 0} entr{(data.count || 0) === 1 ? "y" : "ies"}</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <Table responsive borderless className="align-middle tnt-table mb-0" style={{ fontSize: 13, minWidth: 980 }}>
              <thead>
                <tr>
                  <th style={{ width: 95 }}>Date</th><th>Vehicle</th><th>Category</th><th>Invoice</th><th>Service Type</th>
                  <th className="text-end">Amount</th><th className="text-end">VAT</th><th className="text-end">Total</th>
                  <th>Voucher</th><th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan="10" className="text-center text-muted py-3">Loading…</td></tr>}
                {!loading && !logs.length && <tr><td colSpan="10" className="text-center text-muted py-3">No entries for {month}. Click “Maintenance Entry”.</td></tr>}
                {!loading && logs.map(l => (
                  <tr key={l.id}>
                    <td className="text-nowrap">{fmtDate(l.service_date || l.entry_date)}</td>
                    <td className="fw-500">{l.plate}</td>
                    <td>{l.category || "—"}</td>
                    <td>{l.invoice_no || "—"}</td>
                    <td>{l.service_type || "—"}</td>
                    <td className="text-end">{money(l.amount)}</td>
                    <td className="text-end">{money(l.vat)}</td>
                    <td className="text-end fw-600">{money(l.total)}</td>
                    <td className="text-nowrap">
                      {l.voucher_ref
                        ? <span className="badge bg-soft-success text-success">{l.voucher_ref}</span>
                        : <Button size="sm" color="link" className="p-0" onClick={() => openPost(l)}><i className="bx bx-receipt me-1" />Post</Button>}
                    </td>
                    <td className="text-end text-nowrap">
                      <Button size="sm" color="link" className="p-0 me-2" onClick={() => openEdit(l)}><i className="bx bx-edit" /></Button>
                      <Button size="sm" color="link" className="p-0 text-danger" onClick={() => remove(l)}><i className="bx bx-trash" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody></Card>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>{form.id ? "Edit" : "New"} Maintenance Entry</ModalHeader>
        <ModalBody>
          <Row className="g-2">
            <Col md={4}><FormGroup><Label>Entry Date</Label><Input type="date" bsSize="sm" value={form.entry_date} onChange={e => set("entry_date", e.target.value)} /></FormGroup></Col>
            <Col md={4}><FormGroup><Label>Services Date</Label><Input type="date" bsSize="sm" value={form.service_date} onChange={e => set("service_date", e.target.value)} /></FormGroup></Col>
            <Col md={4}><FormGroup><Label>Invoice No</Label><Input bsSize="sm" value={form.invoice_no} onChange={e => set("invoice_no", e.target.value)} /></FormGroup></Col>
            {companies.length > 1 && (
              <Col md={4}><FormGroup><Label>Company</Label>
                <Input type="select" bsSize="sm" value={vehCompany} onChange={e => setVehCompany(e.target.value)}>
                  <option value="">All companies</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Input></FormGroup></Col>
            )}
            <Col md={companies.length > 1 ? 5 : 6}><FormGroup><Label>Vehicle Plate No<span className="text-danger">*</span></Label>
              <Select classNamePrefix="rs" isClearable isSearchable placeholder="Search & select vehicle…"
                options={vehicleOptions}
                value={vehicleOptions.find(o => String(o.value) === String(form.vehicle_id)) || null}
                onChange={opt => pickVehicle(opt?.value || "")}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} />
            </FormGroup></Col>
            <Col md={3}><FormGroup><Label>Category</Label><Input bsSize="sm" value={selectedVehicle?.vehicle_type || ""} disabled placeholder="—" /></FormGroup></Col>
            <Col md={6}><FormGroup><Label>Driver Name</Label><Input bsSize="sm" value={form.driver_name} onChange={e => set("driver_name", e.target.value)} /></FormGroup></Col>
            <Col md={6}><FormGroup><Label>Services Type</Label><Input bsSize="sm" value={form.service_type} onChange={e => set("service_type", e.target.value)} /></FormGroup></Col>
            <Col md={12}><FormGroup><Label>Description</Label><Input bsSize="sm" value={form.description} onChange={e => set("description", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Odometer</Label><Input type="number" min="0" step="0.01" bsSize="sm" value={form.odometer} onChange={e => set("odometer", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Last Services KM</Label><Input type="number" min="0" step="0.01" bsSize="sm" value={form.last_service_km} onChange={e => set("last_service_km", e.target.value)} /></FormGroup></Col>
            <Col md={2}><FormGroup><Label>Services Amount</Label><Input type="number" min="0" step="0.01" bsSize="sm" value={form.amount} onChange={e => setAmount(e.target.value)} /></FormGroup></Col>
            <Col md={2}><FormGroup><Label>VAT @ 5%</Label><Input type="number" min="0" step="0.01" bsSize="sm" value={form.vat} onChange={e => setVat(e.target.value)} /></FormGroup></Col>
            <Col md={2}><FormGroup><Label>Total Amount</Label><Input type="number" bsSize="sm" value={form.total} disabled /></FormGroup></Col>
            <Col md={6}><FormGroup><Label>Remarks</Label><Input bsSize="sm" value={form.remarks} onChange={e => set("remarks", e.target.value)} /></FormGroup></Col>
            {!form.id && (
              <Col md={6}><FormGroup><Label>Pay from <span className="text-muted" style={{ fontWeight: 400 }}>(optional — auto-posts the voucher)</span></Label>
                {payFromSelect(form.payment_ledger_id, e => set("payment_ledger_id", e.target.value))}
              </FormGroup></Col>
            )}
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" size="sm" onClick={() => setModal(false)}>Cancel</Button>
          <Button color="primary" size="sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!postFor} toggle={() => setPostFor(null)} centered>
        <ModalHeader toggle={() => setPostFor(null)}>Post Payment Voucher</ModalHeader>
        <ModalBody>
          {postFor && (
            <>
              <div className="mb-2 small"><b>{postFor.plate}</b> · {fmtDate(postFor.service_date || postFor.entry_date)} · <b>{money(postFor.total)} AED</b></div>
              <FormGroup><Label>Pay from</Label>{payFromSelect(postLedger, e => setPostLedger(e.target.value))}</FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" size="sm" onClick={() => setPostFor(null)}>Cancel</Button>
          <Button color="primary" size="sm" onClick={doPost} disabled={posting || !postLedger}>{posting ? "Posting…" : "Post voucher"}</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default FleetMaintenance
